import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { FaHome, FaBookOpen, FaUmbrellaBeach } from 'react-icons/fa';
import MyClassesCard from './MyClassesCard';
import AttendanceTodayCard from './AttendanceTodayCard';
import ExamMarkingPipelineCard from './ExamMarkingPipelineCard';
import DashboardStatGroup from './DashboardStatGroup';
import TimetableStatusCard from './TimetableStatusCard';
import Loader from '../../components/Loader/Loader';
import { getTeacherDashboardSummary } from '../../services/DashboardService';
import './Dashboard.css';

const TeacherDashboard = () => {
  const [summary, setSummary] = useState(null);

  const permissions = useMemo(
    () => JSON.parse(localStorage.getItem('permissions')) || [],
    [],
  );
  const can = useCallback((permission) => permissions.includes(permission), [permissions]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await getTeacherDashboardSummary();
      if (response.status === 200) {
        setSummary(response.data.data);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (!summary) return <Loader />;

  const {
    my_classes,
    attendance_today,
    pending_leave_approvals,
    exam_marking_pipeline,
    homework,
    timetable,
  } = summary;

  const canViewAttendance = can('attendance.mark');
  const canViewMarking = can('assessment.marking.view');
  const canViewLeave = can('leave.view');
  const canViewHomework = can('homework.view');
  const canViewTimetable = can('teacher.time_table.view');

  const showAttentionSection = canViewMarking || canViewLeave;
  const showHomeworkSection = canViewHomework || canViewTimetable;

  return (
    <div className="dashboard-page">
      <div className="dash-page-header">
        <div className="dash-page-title d-flex align-items-center gap-2">
          <FaHome />
          <span>Dashboard</span>
        </div>
        <div className="dash-page-subtitle">Your classes and daily tasks at a glance</div>
      </div>

      <div className="row">
        <MyClassesCard classes={my_classes} />
        {canViewAttendance && (
          <AttendanceTodayCard
            attendance={attendance_today}
            classWiseStatus={attendance_today.class_wise_status}
            actionLink="/attendance/create"
          />
        )}
      </div>

      {showAttentionSection && (
        <>
          <div className="dash-section-title">Needs Your Attention</div>
          <div className="row">
            {canViewMarking && (
              <ExamMarkingPipelineCard pipeline={exam_marking_pipeline} link="/assessment/marking/list" />
            )}
            {canViewLeave && (
              <DashboardStatGroup
                title="Leave Approvals"
                icon={<FaUmbrellaBeach />}
                link="/leave/list"
                metrics={[
                  { label: 'Pending', value: pending_leave_approvals, color: 'warning' },
                ]}
              />
            )}
          </div>
        </>
      )}

      {showHomeworkSection && (
        <>
          <div className="dash-section-title">Homework &amp; Timetable</div>
          <div className="row">
            {canViewHomework && (
              <DashboardStatGroup
                title="Homework"
                icon={<FaBookOpen />}
                link="/homework/list"
                metrics={[
                  { label: 'Total Assigned', value: homework.total_assigned },
                  { label: 'Due Today', value: homework.daily_today, color: 'warning' },
                  { label: 'Weekly Active', value: homework.weekly_active, color: 'success' },
                ]}
              />
            )}
            {canViewTimetable && (
              <TimetableStatusCard timetable={timetable} link="/teacher/timetable/list" />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TeacherDashboard;
